import React, { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getCountryForUi } from "../../store/reducer/country/countrySlice";
import {
    getCustomerById,
    updateCustomer,
} from "../../store/reducer/customers/customersSlice";

/* -------------------------------------------------------------------------- */
/*                                COMPONENT                                   */
/* -------------------------------------------------------------------------- */

function EditCustomer() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { options = [] } = useSelector((s) => s.ddOptions);
    const { countries = [] } = useSelector((s) => s.countries);

    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ---------------------------- fetch data ----------------------------- */
    useEffect(() => {
        dispatch(getCustomerById(id)).then((res) => {
            if (res.payload) {
                setCustomerData(res.payload);
                setLoading(false);
            }
        });
        dispatch(getDdOptionForUI());
        dispatch(getCountryForUi());
    }, [dispatch, id]);

    /* --------------------------- form schema ----------------------------- */
    const validationSchema = Yup.object({
        customer_name: Yup.string().required("please enter customer name"),
        trading_name: Yup.string().required("please enter trading name"),
        contact_person: Yup.string().required(
            "please enter contact person name"
        ),
        email: Yup.string()
            .email("invalid email")
            .required("please enter email"),
        customer_type: Yup.string().required("please select customer type"),
        industry: Yup.string().required("please select industry"),
    });

    if (loading || !customerData) {
        return (
            <div className="text-center my-20">
                <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    size="2x"
                    color="#55A14A"
                />
            </div>
        );
    }

    /* ------------------------- initial values ---------------------------- */
    const initialValues = {
        customer_name: customerData.customer_name ?? "",
        trading_name: customerData.trading_name ?? "",
        contact_person: customerData.contact_person ?? "",
        email: customerData.email ?? "",
        phone: customerData.phone ?? "",
        customer_type: customerData.customer_type ?? "",
        industry: customerData.industry ?? "",
        street: customerData.street ?? "",
        city: customerData.city ?? "",
        state: customerData.state ?? "",
        post_code: customerData.post_code ?? "",
        country: customerData.country ?? "",
        status: customerData.status ?? "",
    };

    /* ---------------------------- submit --------------------------------- */
    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        values.updatedBy = JSON.parse(localStorage.getItem("userSession")).id;

        const formData = new FormData();
        Object.entries(values).forEach(([k, v]) => formData.append(k, v));

        const res = await dispatch(updateCustomer({ data: formData, id }));
        if (res.payload) {
            toast.success(res.payload.message);
            navigate("/customers");
        } else {
            toast.error(res.error.message);
        }
    };

    /* ---------------------------------------------------------------------- */
    /*                                 RENDER                                 */
    /* ---------------------------------------------------------------------- */

    return (
        <>
            {/* ----------------------------- header ----------------------------- */}
            <div className="flex justify-between mb-8">
                <div className="flex items-center gap-4">
                    <Link to="/customers">
                        <div className="bg-[#F8F8F8] p-2 rounded-[10px]">
                            <img
                                src={`${process.env.PUBLIC_URL}/icons/back.png`}
                                alt="back"
                                className="w-6"
                            />
                        </div>
                    </Link>
                    <div>
                        <p className="text-sm leading-4 text-[#9E9E9E]">
                            Customer / Edit
                        </p>
                        <h1 className="text-2xl font-bold leading-8">
                            Edit Customer
                        </h1>
                    </div>
                </div>
            </div>

            {/* ------------------------ sub-navigation -------------------------- */}
            <div className="flex gap-6 bg-[#61b35436] p-2 rounded-lg mb-10 overflow-auto">
                {[
                    { to: `/customer/${id}`, label: "Customer Detail" },
                    { to: `/customer/${id}/contacts`, label: "Contacts" },
                    { to: `/customer/${id}/site`, label: "Sites" },
                ].map(({ to, label }) => (
                    <Link to={to} key={to}>
                        <div
                            className={`font-Inter text-[18px] font-[400] px-5 py-2 rounded-lg
              ${
                  location.pathname === to
                      ? "bg-[#55A14A] text-white"
                      : "text-black hover:bg-[#55A14A] hover:text-white"
              }`}
                        >
                            {label}
                        </div>
                    </Link>
                ))}
            </div>

            {/* ------------------------------ form ------------------------------ */}
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, setFieldValue }) => (
                    <Form>
                        {/* main grid */}
                        <div className="bg-[#F8F8F8] p-6 rounded-2xl">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {/* ---------- column 1 ---------- */}
                                <div className="flex flex-col gap-4">
                                    <FieldBox
                                        label="Customer Name"
                                        name="customer_name"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="Trading Name"
                                        name="trading_name"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="Street"
                                        name="street"
                                        as="textarea"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="City"
                                        name="city"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="State"
                                        name="state"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="Post Code"
                                        name="post_code"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <SelectBox
                                        label="Country"
                                        name="country"
                                        options={countries.map((c) => ({
                                            value: c.id,
                                            label: c.short_name,
                                        }))}
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                </div>

                                {/* ---------- column 2 ---------- */}
                                <div className="flex flex-col gap-4">
                                    <FieldBox
                                        label="Contact Person"
                                        name="contact_person"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="Email"
                                        name="email"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <FieldBox
                                        label="Phone"
                                        name="phone"
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <label className="text-[15px] text-[#9E9E9E]">
                                        Status
                                    </label>
                                    <label className="inline-flex items-center px-5 py-2 bg-white rounded-[12px] text-gray-800">
                                        <input
                                            type="checkbox"
                                            name="status"
                                            className="hidden peer"
                                            defaultChecked={
                                                initialValues.status ===
                                                "Active"
                                            }
                                            onChange={(e) =>
                                                setFieldValue(
                                                    "status",
                                                    e.target.checked
                                                        ? "Active"
                                                        : "Inactive"
                                                )
                                            }
                                        />
                                        <span className="px-4 py-1 border border-gray-600 rounded-lg peer-checked:border-0">
                                            Inactive
                                        </span>
                                        <span className="px-4 py-1 rounded-lg border border-transparent peer-checked:border-gray-600">
                                            Active
                                        </span>
                                    </label>
                                </div>

                                {/* ---------- column 3 ---------- */}
                                <div className="flex flex-col gap-4">
                                    <SelectBox
                                        label="Customer Type"
                                        name="customer_type"
                                        options={options
                                            .filter(
                                                (o) =>
                                                    o.ui_name === "Customer" &&
                                                    o.field_name ===
                                                        "customer_type"
                                            )
                                            .sort((a, b) => a.sort_field - b.sort_field)
                                            .map((o) => ({
                                                value: o.id,
                                                label: `${o.field_code} ${o.field_value}`,
                                            }))}
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                    <SelectBox
                                        label="Customer Industry"
                                        name="industry"
                                        options={options
                                            .filter(
                                                (o) =>
                                                    o.ui_name === "Customer" &&
                                                    o.field_name === "industry"
                                            )
                                            .sort((a, b) => a.sort_field - b.sort_field)
                                            .map((o) => ({
                                                value: o.id,
                                                label: o.field_code,
                                            }))}
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* submit */}
                        <div className="bg-[#F8F8F8] mt-5 p-4 rounded-[12px]">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full md:w-auto py-3 px-12 rounded-[12px] text-lg text-white ${
                                    isSubmitting
                                        ? "bg-gray-300"
                                        : "bg-[#1C1C20]"
                                }`}
                            >
                                {isSubmitting ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
}

/* -------------------------------------------------------------------------- */
/*                               HELPER INPUTS                                */
/* -------------------------------------------------------------------------- */

const FieldBox = ({
    label,
    name,
    as = "input",
    errors,
    touched,
    setFieldValue,
}) => (
    <div>
        <label htmlFor={name} className="text-[15px] text-[#9E9E9E]">
            {label}
        </label>
        <Field
            as={as}
            id={name}
            name={name}
            className="w-full bg-white border border-gray-200 rounded-[12px] py-3 px-4 leading-tight focus:outline-none focus:border-gray-500"
            onChange={(e) => setFieldValue(name, e.target.value)}
        />
        {errors?.[name] && touched?.[name] && (
            <p className="text-red-700">{errors[name]}</p>
        )}
    </div>
);

const SelectBox = ({
    label,
    name,
    options,
    errors,
    touched,
    setFieldValue,
}) => (
    <div>
        <label htmlFor={name} className="text-[15px] text-[#9E9E9E]">
            {label}
        </label>
        <div className="relative">
            <Field
                as="select"
                id={name}
                name={name}
                className="w-full bg-white border border-gray-200 rounded-[12px] py-3 px-4 pr-12 leading-tight focus:outline-none focus:border-gray-500"
                onChange={(e) => setFieldValue(name, e.target.value)}
            >
                <option value="">Select {label.toLowerCase()}</option>
                {options.map(({ value, label }) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </Field>
        </div>
        {errors?.[name] && touched?.[name] && (
            <p className="text-red-700">{errors[name]}</p>
        )}
    </div>
);

export default EditCustomer;
